import { wordCharacters } from "../fuxi/word-characters.js";
import { optionsOf } from "./lianxi-yufa-item.js";

/**
 * Showing the sentence a contrast or a placement question would be answered
 * with, as the reader chooses (CB-23).
 *
 * A contrast draws the site's 填空 boxes where the missing word goes, as a gap
 * item does, and choosing a word moves it into them. Choosing Nothing needed
 * closes the boxes up. A placement draws the word over the sentence and a
 * lettered slot at every place it may go, and choosing a slot moves the word
 * down into it. Either way the reader sees the whole sentence before Check,
 * which is what the letters alone did not show. The word moves for a quarter of
 * a second, and not at all for a reader whose system asks for less motion.
 */

/** The 填空 boxes a contrast's word goes into. */
const blank = "[data-cb-yufa-blank]";

/** What a contrast's boxes carry once Nothing needed is chosen. */
const nothingAttribute = "data-cb-yufa-nothing";

/** Whether the page asks for less motion. */
function stillness(form: HTMLFormElement): boolean {
  return (
    form.ownerDocument.defaultView?.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches === true
  );
}

/** Move `element` from where `from` is to where it is now. */
function glide(element: HTMLElement, from: DOMRect): void {
  const to = element.getBoundingClientRect();

  if (
    to.width === 0 ||
    from.width === 0 ||
    typeof element.animate !== "function"
  ) {
    return;
  }

  element.animate(
    [
      {
        transform: `translate(${String(from.left + from.width / 2 - to.left - to.width / 2)}px, ${String(from.top + from.height / 2 - to.top - to.height / 2)}px)`,
        opacity: 0.5,
      },
      { transform: "translate(0, 0)", opacity: 1 },
    ],
    { duration: 260, easing: "ease-out" },
  );
}

/**
 * The characters a piece of Chinese shows in the reader's writing, without
 * the reading over them.
 */
function shownCharacters(within: Element): string {
  const page = within.ownerDocument.documentElement;
  const writing = page.dataset["cbWriting"] ?? "zh-CN";
  const pieces = [...within.querySelectorAll<HTMLElement>(".hanzi")];
  const piece =
    pieces.find((each) =>
      (each.dataset["cbWriting"] ?? "").split(" ").includes(writing),
    ) ?? pieces[0];

  if (piece === undefined) {
    return "";
  }

  const bare = piece.cloneNode(true) as HTMLElement;

  for (const reading of bare.querySelectorAll("rt, rp")) {
    reading.remove();
  }

  return bare.textContent.trim();
}

/** Fill a contrast's boxes with the word chosen, or close them for none. */
function fillBlank(form: HTMLFormElement, chosen: HTMLInputElement): void {
  const holder = form.querySelector<HTMLElement>(blank);

  if (holder === null) {
    return;
  }

  const word = shownCharacters(
    chosen.closest("label") ?? chosen.parentElement ?? chosen,
  );
  const characters = wordCharacters(word);
  const from = chosen.closest("label")?.getBoundingClientRect();

  holder.toggleAttribute(nothingAttribute, characters.length === 0);
  holder
    .querySelector(".cb-fuxi-hole")
    ?.setAttribute(
      "aria-label",
      characters.length === 0 ? "no word" : characters.join(""),
    );

  for (const [at, gap] of [
    ...holder.querySelectorAll<HTMLElement>(".cb-fuxi-gap"),
  ].entries()) {
    const character = characters[at];

    gap.hidden = characters.length > 0 && character === undefined;

    if (character === undefined) {
      gap.replaceChildren();

      continue;
    }

    const ink = form.ownerDocument.createElement("span");

    ink.className = "lianxi-yufa-gap-ink";
    ink.textContent = character;
    gap.replaceChildren(ink);

    if (from !== undefined && !stillness(form)) {
      glide(ink, from);
    }
  }
}

/** Move a placement's word down into the slot chosen. */
function dropWord(form: HTMLFormElement, chosen: HTMLInputElement): void {
  const word = form.querySelector<HTMLElement>("[data-cb-yufa-word]");
  const landed = chosen
    .closest("label")
    ?.querySelector<HTMLElement>(".lianxi-yufa-slot-word");

  if (word === null || landed === null || landed === undefined) {
    return;
  }

  if (!stillness(form)) {
    glide(landed, word.getBoundingClientRect());
  }
}

/**
 * Show the sentence the option chosen on an item would make, where the item is
 * a contrast or a placement. Any other item is left alone.
 * @param form - The item.
 */
export function showPlaced(form: HTMLFormElement): void {
  const chosen = optionsOf(form).find((radio) => radio.checked);
  const kind = form.dataset["cbYufaKind"];

  if (chosen === undefined) {
    return;
  }

  if (kind === "contrast") {
    fillBlank(form, chosen);
  } else if (kind === "placement") {
    dropWord(form, chosen);
  }
}

/**
 * Put a contrast's boxes back empty, for the question asked again.
 * @param form - The item.
 */
export function clearPlaced(form: HTMLFormElement): void {
  const holder = form.querySelector<HTMLElement>(blank);

  if (holder === null) {
    return;
  }

  holder.removeAttribute(nothingAttribute);
  holder
    .querySelector(".cb-fuxi-hole")
    ?.setAttribute("aria-label", "the missing word");

  for (const gap of holder.querySelectorAll<HTMLElement>(".cb-fuxi-gap")) {
    gap.hidden = false;
    gap.replaceChildren();
  }
}
